import * as Sentry from "@sentry/react";

import { exportH2kFile, validateH2kExport } from "utils/h2kinout/api";

import { summarizeExportErrors } from "./summarizeExportErrors";

const parseH2kFromExport = (returnString) => {
    if (!returnString) {
        return "";
    }

    const splitPackage = returnString.split("\r");
    const startInd = splitPackage.findIndex((el) => el.includes("<HouseFile")) || 3;
    const endInd = splitPackage.findIndex((el) => el.includes("</HouseFile>")) || 3;

    let h2kData = endInd === 3 ? splitPackage[3] : splitPackage.slice(startInd, endInd + 1).join("\r");

    if (h2kData.charAt(0) === "\r" || h2kData.charAt(0) === "\n") {
        h2kData = h2kData.substring(1);
    }

    return h2kData;
};

/**
 * Reports a "Results not found" event to Sentry with model context, H2K attachment,
 * and a summary of the validation errors shown to the user in the ErrorList UI.
 */
export const captureResultsNotFound = async ({
    modelId,
    modelData = {},
    user: { email, uid, name } = {},
    source,
    exportErrorsFromState = [],
}) => {
    const { major: h2kMajor, minor: h2kMinor } = modelData?.h2kVersion || {};
    const h2kVersion = `v${h2kMajor}-${h2kMinor}`;

    const exportOptions = {
        modelId,
        returnType: "XML",
        h2kVersion,
        upgradePackage: {},
        appendResults: false,
        applyUpgradesToBase: false,
        excludeClientData: true,
    };

    const [returnString, validationErrors] = await Promise.all([
        exportH2kFile(exportOptions),
        validateH2kExport({ modelId, h2kVersion, isSimulation: true }).catch(() => []),
    ]);

    const h2kData = parseH2kFromExport(returnString);
    const errorList = validationErrors?.length ? validationErrors : exportErrorsFromState;
    const exportErrorSummary = summarizeExportErrors(errorList);

    Sentry.withScope((scope) => {
        const sentryError = new Error(`Results not found in ${source}()`);
        sentryError.name = `Results not found in ${modelId}`;

        scope.setFingerprint(["results-not-found", modelId]);
        scope.setTag("results_not_found", true);
        scope.setTag("modelId", modelId);
        scope.setTag("results_not_found_source", source);
        scope.setExtras({
            modelId,
            modelData: {
                ...modelData,
                general: {
                    ...modelData.general,
                    client: {},
                },
            },
            exportErrorSummary,
        });
        scope.setTransactionName("Results not found");
        scope.setLevel("warning");
        scope.setUser({ email, uid, name });

        if (h2kData) {
            scope.addAttachment({
                filename: `model_${modelId}.h2k`,
                data: h2kData,
                contentType: "application/xml",
            });
        }

        scope.captureException(sentryError);
    });
};
